<script setup>
import { nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import 'number-flow';

const props = defineProps({
  value: { type: [Number, String], required: true },
  format: { type: Object, default: undefined },
  locales: { type: [String, Array], default: undefined },
  prefix: { type: String, default: undefined },
  suffix: { type: String, default: undefined },
  transformTiming: { type: Object, default: undefined },
  spinTiming: { type: Object, default: undefined },
  opacityTiming: { type: Object, default: undefined },
  trend: { type: [Boolean, Number, Function], default: undefined },
  animated: { type: Boolean, default: true },
  digits: { type: Object, default: undefined },
  respectMotionPreference: { type: Boolean, default: true },
  plugins: { type: Array, default: () => [] },
  willChange: { type: Boolean, default: false },
});

const emit = defineEmits(['animationsstart', 'animationsfinish']);
const el = ref(null);

function applyAll() {
  if (!el.value) return;
  const n = el.value;
  if (props.locales !== undefined) n.locales = props.locales;
  if (props.format !== undefined) n.format = props.format;
  if (props.prefix !== undefined) n.numberPrefix = props.prefix;
  if (props.suffix !== undefined) n.numberSuffix = props.suffix;
  if (props.transformTiming !== undefined)
    n.transformTiming = props.transformTiming;
  if (props.spinTiming !== undefined) n.spinTiming = props.spinTiming;
  if (props.opacityTiming !== undefined) n.opacityTiming = props.opacityTiming;
  if (props.trend !== undefined) n.trend = props.trend;
  n.animated = props.animated;
  n.respectMotionPreference = props.respectMotionPreference;
  if (props.plugins && props.plugins.length) n.plugins = props.plugins;
  if (props.digits !== undefined) n.digits = props.digits;
  props.willChange
    ? n.setAttribute('data-will-change', '')
    : n.removeAttribute('data-will-change');
  n.update(props.value);
}

onMounted(() => {
  if (!el.value) return;
  el.value.addEventListener('animationsstart', e => emit('animationsstart', e));
  el.value.addEventListener('animationsfinish', e =>
    emit('animationsfinish', e)
  );
  nextTick(applyAll);
});

onBeforeUnmount(() => {
  if (!el.value) return;
  el.value.removeEventListener('animationsstart', emit);
  el.value.removeEventListener('animationsfinish', emit);
});

// eslint-disable-next-line style/max-statements-per-line
watch(
  () => props.value,
  v => {
    if (el.value) el.value.update(v);
  }
);
watch(
  () => [
    props.locales,
    props.format,
    props.prefix,
    props.suffix,
    props.transformTiming,
    props.spinTiming,
    props.opacityTiming,
    props.trend,
    props.animated,
    props.respectMotionPreference,
    props.plugins,
    props.willChange,
  ],
  applyAll,
  { deep: true }
);
</script>

<template>
  <number-flow ref="el" v-bind="$attrs" class="cs-animated-number" />
</template>
